import { Button, Tooltip } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import classNames from "classnames";
import React, { FocusEvent, useMemo, useState } from "react";
// Formik imports
import CircularProgress from "@mui/material/CircularProgress";
import { Theme } from "@mui/material/styles";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";

export interface ISwitchFieldProps {
  input?: any;
  custom?: any;
  name: string;
  value?: boolean | null;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: boolean) => void;
  label: string;
  className?: string;
  switchlabels: string[];
  switchtooltips: string[];
  allowNull: boolean;
  disabled?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  showSpinner?: boolean;
  inlineSx?: any;
}

// SWITCHFIELD COMPONENT
export const SwitchField = (props: ISwitchFieldProps): JSX.Element => {
  const {
    name,
    allowNull,
    switchtooltips,
    switchlabels,
    label,
    onFocus,
    onBlur,
    value,
    onChange,
    className,
    disabled,
    meta,
    showSpinner,
    inlineSx,
  } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const tooltips =
    Array.isArray(switchtooltips) && switchtooltips.length === 2 ? switchtooltips : ["option 1", "option 2"];
  const labels = Array.isArray(switchlabels) && switchlabels.length === 2 ? switchlabels : ["1", "2"];

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    if (onBlur && typeof onBlur === "function") {
      onBlur(e);
    }
  };

  const handleToggle = (buttonName: "button1" | "button2") => (event: any) => {
    const primaryButton: boolean = buttonName === "button1" ? true : false;
    let newValue: boolean;
    switch (true) {
      case allowNull && primaryButton === value:
        newValue = null;
        break;
      case buttonName === "button1":
        newValue = true;
        break;
      case buttonName === "button2":
        newValue = false;
        break;
      default:
        break;
    }
    // Fire off the supplied onChange from props and not the formik field one (it breaks as it wants an event and not a value)
    if (onChange && typeof onChange === "function") {
      onChange(newValue);
    }
  };

  const hasError = meta?.error ? true : false;

  const SwitchFieldStyles = (theme: any): any => ({
    root: {
      display: "flex",
      justifyContent: "center",
      margin: "5px",
      flexWrap: "wrap",
      width: "calc(100% - 10px)",
      "&.split_label_input": {
        width: "calc(100% - 10px)",
        flexDirection: "row",
        alignItems: "center",
        "& label, & .switch-buttons": {
          display: "flex",
          width: "50%",
          maxHeight: 24,
        },
      },
      "& label": {
        position: "relative",
        width: "80px",
        textAlign: "right",
        transform: "none",
        transition: "none",
        color: theme.custom.forms.pristineColor,
        fontSize: 12,
        lineHeight: "12px",
        margin: "5px 10px auto auto",
      },
      "& .switch-buttons": {
        display: "flex",
        "& button": {
          height: "26px",
          minWidth: "42px",
          padding: "0px 2px",
          fontSize: 10,
          "&.disabled": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
        },
        "& button.button1": {
          borderRadius: "2px 0px 0px 2px",
        },
        "& button.button2": {
          borderRadius: "0px 2px 2px 0px",
        },
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.error": {
        "& label": {
          color: theme.custom.forms.errorColor + "!important",
        },
      },
      "&.dirty": {
        "& label": {
          color: theme.custom.forms.dirtyColor,
        },
        "& .switch-buttons button.MuiButton-contained": {
          backgroundColor: theme.custom.forms.dirtyColor,
          color: theme.palette.common.black,
        },
      },
      "&.focused": {
        "& label": {
          color: theme.custom.forms.focusedColor,
        },
        "& .switch-buttons button.MuiButton-contained": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      },
      "&.time_mode_selector": {
        margin: "5px",
        flexDirection: "row",
        alignItems: "flex-start",
        "& label": {
          width: "auto",
          color: theme.palette.primary.contrastText,
          fontSize: 12,
          fontWeight: 300,
          margin: "0px 10px",
        },

        "&.focused label": { color: "unset" },
        "& .switch-buttons": {
          margin: "-4px 4px -4px 0px",
          "& button": { height: "14px" },
        },

        "&.active": {
          "& .switch-buttons button.disabled": {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.background.default,
          },
        },
      },
    },
  });

  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error"
      )}
      sx={(theme: Theme) => ({ ...SwitchFieldStyles(theme).root, ...(inlineSx ?? {}) })}
      error={hasError}
    >
      {label && <InputLabel htmlFor={"id-" + name + "-date"}>{label}</InputLabel>}

      <div className="switch-buttons">
        <Tooltip title={tooltips[0]} disableInteractive={true}>
          <span>
            <Button
              {...({
                id: "switch1_btn",
                variant: value === true ? "contained" : "outlined",
                className: "button1",
                color: "primary",
                classes: { disabled: "disabled" },
                disabled: !allowNull && value === true,
                onClick: handleToggle("button1"),
                onBlur: handleBlur,
                onFocus: handleFocus,
              } as React.ComponentProps<typeof Button>)}
            >
              {labels[0]}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={tooltips[1]} disableInteractive={true}>
          <span>
            <Button
              {...({
                id: "switch2_btn",
                variant: value === false ? "contained" : "outlined",
                className: "button2",
                color: "primary",
                classes: { disabled: "disabled" },
                disabled: !allowNull && value === false,
                onClick: handleToggle("button2"),
                onBlur: handleBlur,
                onFocus: handleFocus,
              } as React.ComponentProps<typeof Button>)}
            >
              {labels[1]}
              {showSpinner && <CircularProgress color="inherit" style={{ width: 10, height: 10, marginLeft: 8 }} />}
            </Button>
          </span>
        </Tooltip>
      </div>
      <FieldError meta={meta} />
    </FormControl>
  );
};
